// --- START OF FILE public/js/firebase/auth.js ---

/**
 * Gestione autenticazione Firebase unificata v1.8 - FIX: Google Registration Event
 * MODIFICATO: La funzione loginWithGoogle ora controlla l'esistenza del documento utente
 *             in Firestore per determinare se si tratta di una nuova registrazione. Se l'utente
 *             è nuovo, viene emesso l'evento 'userRegistered' per sbloccare l'achievement.
 * MANTIENE: Tutta la logica precedente.
 */

import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail as firebaseSendPasswordResetEmail,
    signInWithPopup,
    GoogleAuthProvider,
    signOut,
    updateProfile,
    sendEmailVerification
} from "firebase/auth";

import {
    doc,
    getDoc,
    setDoc,
    serverTimestamp
} from "firebase/firestore";


class AuthManager {
    constructor() {
        if (!window.firebaseServices || !window.firebaseServices.auth) {
            console.error("[AuthMgr Constructor Error v1.8] Firebase Auth service modulare non disponibile!");
            this.auth = null;
            this.initializationError = true;
            throw new Error("Auth service non disponibile per AuthManager constructor.");
        }
        this.auth = window.firebaseServices.auth;

        if (!window.firebaseServices.db) {
            console.warn("[AuthMgr Constructor v1.8] Firebase Firestore service non disponibile. Funzionalità DB limitate.");
            this.db = null;
        } else {
            this.db = window.firebaseServices.db;
        }

        this.initializationError = false;
        this.authStateChanged = false;
        this.currentUser = this.auth.currentUser;
        this.playMode = localStorage.getItem('playMode') || 'guest';
        this.googleProvider = new GoogleAuthProvider();
        this.profileManager = null;
        console.log("[AuthMgr] Constructor (v1.8 - FIX: Google Registration Event)");

        onAuthStateChanged(this.auth, user => {
            console.log("[AuthMgr onAuthStateChanged v1.8] Triggered. User:", user ? user.uid : 'null');
            const wasAuth = this.isAuthenticated();
            this.currentUser = user;
            this.authStateChanged = true;
            if (user) {
                this.playMode = 'authenticated';
                localStorage.setItem('playMode', 'authenticated');
                localStorage.setItem('userId', user.uid);
                const displayName = user.displayName || (user.email ? user.email.split('@')[0].toUpperCase() : 'PLAYER');
                localStorage.setItem('userDisplayName', displayName);
                console.log('[AuthMgr onAuthStateChanged v1.8] State -> Logged In:', displayName);
                this.dispatchAuthEvent('auth-success', { user: user });
            } else {
                this.playMode = 'guest';
                localStorage.setItem('playMode', 'guest');
                localStorage.removeItem('userId');
                localStorage.removeItem('userDisplayName');
                console.log('[AuthMgr onAuthStateChanged v1.8] State -> Logged Out / Guest');
                this._getProfileManager()?.clearUserCache();
                if (wasAuth) {
                    this.dispatchAuthEvent('auth-logout');
                }
            }
        });
    }

    _getProfileManager() {
        if (!this.profileManager && window.profileManager) {
            this.profileManager = window.profileManager;
        }
        return this.profileManager;
    }

    dispatchAuthEvent(eventName, data = {}) {
        console.log("[AuthMgr dispatchAuthEvent v1.8]", eventName, data);
        try {
            const event = new CustomEvent('firebase-auth', { detail: { type: eventName, ...data } });
            document.dispatchEvent(event);
        } catch (e) {
            console.error('[AuthMgr dispatchAuthEvent v1.8] Error:', e);
        }
    }

    initEventListeners() {
        console.log('[AuthMgr initEventListeners v1.8] (Solo per toggle password landing)');
        this.setupPasswordToggles();
    }

    setupPasswordToggles() {
        console.log('[AuthMgr setupPasswordToggles v1.8]');
        const eyeOpenSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path><circle cx="12" cy="12" r="3"></circle></svg>`;
        const eyeClosedSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path><line x1="2" x2="22" y1="2" y2="22"></line></svg>`;

        const setupToggle = (inputId, toggleId) => {
            const input = document.getElementById(inputId);
            const button = document.getElementById(toggleId);
            if (input && button) {
                const newButton = button.cloneNode(true);
                button.parentNode.replaceChild(newButton, button);
                newButton.innerHTML = eyeClosedSVG;
                if (input.type !== 'password') input.type = 'password';
                newButton.addEventListener('click', function () {
                    if (input.type === 'password') {
                        input.type = 'text';
                        newButton.innerHTML = eyeOpenSVG;
                    } else {
                        input.type = 'password';
                        newButton.innerHTML = eyeClosedSVG;
                    }
                });
            }
        };
        setupToggle('login-password', 'login-password-toggle');
        setupToggle('register-password', 'register-password-toggle');
    }

    async loginWithEmailPassword(email, password, errorElement) {
        console.log("[AuthMgr loginWEP v1.8] attempt");
        if (!email || !password) {
            if (errorElement) errorElement.textContent = 'Inserisci email e password.';
            this.dispatchAuthEvent('login-error', { error: 'missing-credentials' });
            return Promise.reject('missing-credentials');
        }
        try {
            if (errorElement) errorElement.textContent = '';
            const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
            console.log("[AuthMgr loginWEP v1.8] SUCCESS");
            this.dispatchAuthEvent('login-success', { user: userCredential.user });
            return Promise.resolve();
        } catch (error) {
            console.error('[AuthMgr loginWEP v1.8] FAIL:', error);
            if (errorElement) {
                let msg = 'Errore durante l\'accesso.';
                if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
                    msg = 'Credenziali non valide o utente non trovato.';
                } else if (error.code === 'auth/invalid-email') {
                    msg = 'Formato email non valido.';
                }
                errorElement.textContent = msg;
            }
            this.dispatchAuthEvent('login-error', { error: error.code });
            return Promise.reject(error);
        }
    }

    async registerWithEmailPassword(email, password, name, errorElement) {
        console.log("[AuthMgr registerWEP v1.8] attempt");
        if (!email || !password) {
            if (errorElement) errorElement.textContent = 'Inserisci email e password.';
            this.dispatchAuthEvent('register-error', { error: 'missing-credentials' });
            return Promise.reject('missing-credentials');
        }
        if (password.length < 6) {
            if (errorElement) errorElement.textContent = 'La password deve essere di almeno 6 caratteri.';
            this.dispatchAuthEvent('register-error', { error: 'auth/weak-password' });
            return Promise.reject('auth/weak-password');
        }
        if (!name || name.trim().length === 0) {
            if (errorElement) errorElement.textContent = 'Inserisci un nome giocatore.';
            this.dispatchAuthEvent('register-error', { error: 'missing-name' });
            return Promise.reject('missing-name');
        }
        try {
            if (errorElement) errorElement.textContent = '';
            const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
            await updateProfile(userCredential.user, { displayName: name });
            try { await sendEmailVerification(userCredential.user); } catch (verifError) { console.warn("[AuthMgr registerWEP v1.8] Invio email di verifica fallito:", verifError); }
            if (this.db) {
                try {
                    const userDocRef = doc(this.db, "users", userCredential.user.uid);
                    await setDoc(userDocRef, { displayName: name, email: email, createdAt: serverTimestamp(), lastLogin: serverTimestamp() });
                } catch (dbError) { console.warn("[AuthMgr registerWEP v1.8] Salvataggio utente Firestore fallito:", dbError); }
            }
            console.log("[AuthMgr registerWEP v1.8] SUCCESS");
            this.dispatchAuthEvent('userRegistered', { user: userCredential.user });
            this.dispatchAuthEvent('register-success', { user: userCredential.user });
            return Promise.resolve();
        } catch (error) {
            console.error('[AuthMgr registerWEP v1.8] FAIL:', error);
            if (errorElement) {
                let msg = 'Errore durante la registrazione.';
                if (error.code === 'auth/email-already-in-use') {
                    msg = 'Email già in uso.';
                } else if (error.code === 'auth/invalid-email') {
                    msg = 'Formato email non valido.';
                }
                errorElement.textContent = msg;
            }
            this.dispatchAuthEvent('register-error', { error: error.code });
            return Promise.reject(error);
        }
    }

    async sendPasswordResetEmail(email, errorElement, successElement) {
        console.log("[AuthMgr sendPWReset v1.8] attempt");
        if (!email) {
            if (errorElement) errorElement.textContent = 'Inserisci l\'email.';
            this.dispatchAuthEvent('reset-error', { error: 'missing-email' });
            return Promise.reject('missing-email');
        }
        try {
            if (errorElement) errorElement.textContent = '';
            await firebaseSendPasswordResetEmail(this.auth, email);
            console.log("[AuthMgr sendPWReset v1.8] SUCCESS");
            this.dispatchAuthEvent('reset-success');
            return Promise.resolve();
        } catch (error) {
            console.error('[AuthMgr sendPWReset v1.8] FAIL:', error);
            this.dispatchAuthEvent('reset-error', { error: error.code });
            return Promise.reject(error);
        }
    }
    
    async loginWithGoogle() {
        console.log("[AuthMgr loginWGoogle v1.8] attempt");
        this.dispatchAuthEvent('auth-loading');
        try {
            const result = await signInWithPopup(this.auth, this.googleProvider);
            const user = result.user;
            let isNewUser = false;

            if (this.db && user) {
                const userDocRef = doc(this.db, "users", user.uid);
                
                // 1. Controlla se l'utente esiste già
                const docSnap = await getDoc(userDocRef);
                if (!docSnap.exists()) {
                    isNewUser = true;
                    console.log(`[AuthMgr loginWGoogle v1.8] Nuovo utente rilevato (ID: ${user.uid}).`);
                }

                // 2. Scrivi o aggiorna il documento utente
                try {
                    const dataToSet = {
                        displayName: user.displayName || user.email.split('@')[0].toUpperCase(),
                        email: user.email,
                        photoURL: user.photoURL || null,
                        lastLogin: serverTimestamp()
                    };
                    // Se è un nuovo utente, imposta anche createdAt
                    if (isNewUser) {
                        dataToSet.createdAt = serverTimestamp();
                    }
                    await setDoc(userDocRef, dataToSet, { merge: true });
                } catch (dbError) {
                    console.warn("[AuthMgr loginWGoogle v1.8] Salvataggio/aggiornamento Firestore fallito:", dbError);
                }
            }
            
            console.log("[AuthMgr loginWGoogle v1.8] SUCCESS");

            // 3. Emetti l'evento di registrazione SOLO se è un nuovo utente
            if (isNewUser) {
                this.dispatchAuthEvent('userRegistered', { user: user });
            }

            // 4. Emetti sempre l'evento di successo generico
            this.dispatchAuthEvent('google-success', { user: user });
            return Promise.resolve();
        } catch (error) {
            console.error('[AuthMgr loginWGoogle v1.8] FAIL:', error);
            this.dispatchAuthEvent('google-error', { error: error.code });
            return Promise.reject(error);
        }
    }

    async logout() {
        console.log("[AuthMgr logout v1.8] attempt");
        if (this.initializationError || !this.auth) {
            console.error("[AuthMgr Logout v1.8] Auth service non disponibile.");
            return Promise.reject('auth-unavailable');
        }
        try {
            this.dispatchAuthEvent('auth-loading');
            await signOut(this.auth);
            console.log('[AuthMgr logout v1.8] User signed out from Firebase.');
            this.dispatchAuthEvent('logout-success');
            return Promise.resolve();
        } catch (error) {
            console.error('[AuthMgr Logout v1.8] FAIL:', error);
            this.dispatchAuthEvent('logout-error', { error: error.code });
            return Promise.reject(error);
        }
    }

    getUserDisplayName() {
        const storedName = localStorage.getItem('userDisplayName');
        if (this.currentUser) {
            const firebaseName = this.currentUser.displayName || (this.currentUser.email ? this.currentUser.email.split('@')[0].toUpperCase() : null);
            if (firebaseName && firebaseName.trim() !== '' && firebaseName !== storedName) {
                localStorage.setItem('userDisplayName', firebaseName);
                return firebaseName;
            }
        }
        return storedName || 'PLAYER';
    }

    getUserId() {
        const storedId = localStorage.getItem('userId');
        if (this.currentUser) {
            if (this.currentUser.uid !== storedId) {
                localStorage.setItem('userId', this.currentUser.uid);
            }
            return this.currentUser.uid;
        }
        return storedId || null;
    }
    
    isAuthenticated() { return !!this.currentUser; }
    isGuest() { return !this.isAuthenticated(); }
    
    async waitForAuthReady() {
        if (this.authStateChanged && typeof this.currentUser !== 'undefined') {
            return Promise.resolve();
        }
        return new Promise(resolve => {
            if (!this.auth) { resolve(); return; }
            const unsubscribe = onAuthStateChanged(this.auth, user => {
                unsubscribe();
                this.authStateChanged = true;
                console.log("[AuthMgr waitForAuthReady v1.8] Auth state pronto (currentUser:", this.currentUser ? this.currentUser.uid : "null", ")");
                resolve();
            });
        });
    }
}

window.AuthManager = AuthManager;
console.log("[AuthMgr Modular] AuthManager CLASS (v1.8 - FIX: Google Registration Event) defined on window.");

// --- END OF FILE public/js/firebase/auth.js ---